<template>
    <span>
        <div v-if="show">
            <v-tabs grow v-model="activeTab" color="accent" class="mb-4">
                <v-tab
                    v-for="tab of tabs"
                    :key="tab.id"
                    class="text-uppercase text-label-large text-medium-emphasis"
                >
                    <v-badge
                        v-if="getBadge(tab) !== 0"
                        color="typography1"
                        inline
                        :content="getBadge(tab)"
                    >
                        <span class="mr-1">{{ tab.name }}</span>
                    </v-badge>
                    <span v-else> {{ tab.name }} </span>
                </v-tab>
            </v-tabs>

            <div
                :style="
                    'white-space: normal !important; overflow: auto; word-break: normal; min-height: 300px; height: ' +
                    height
                "
                class="pt-4"
            >
                <span v-show="activeTab === 0">
                    <span v-if="!duplicatedInfo">
                        <p class="text-title-medium text--typography1">
                            {{ calc.name }}
                        </p>
                        <p
                            class="text-body-large text--typography1"
                            style="word-break: normal !important"
                        >
                            {{ calc.description }}
                        </p>
                    </span>
                    <span v-if="calc.additionalInformationLinks">
                        <p class="text-title-medium text--typography1">Links</p>
                        <div class="mb-4">
                            <div
                                v-for="link in calc.additionalInformationLinks"
                                :key="link"
                                class="text-body-large text--typography1"
                            >
                                <a :href="link" target="_blank">{{ link }}</a>
                            </div>
                        </div>
                    </span>
                    <p class="text-title-medium text--typography1">
                        Information
                    </p>
                    <p class="text-body-large text--typography1">
                        Library version: {{ libraryVersion.version }}
                    </p>
                    <p
                        v-if="calc.user"
                        class="
                            text-body-large text--typography1
                            d-flex
                            align-center
                        "
                    >
                        Created by: {{ calc.user.displayName }}
                        <v-btn
                            class="pa-0"
                            size="small"
                            variant="plain"
                            density="compact"
                            icon
                            :href="'mailto:' + calc.user.mail"
                        >
                            <v-icon size="small">mdi-email</v-icon>
                        </v-btn>
                    </p>
                    <div class="d-flex" v-if="additionalAuthors.length > 0">
                        <p>Additional Authors:</p>
                        <ul>
                            <li
                                v-for="author in additionalAuthors"
                                :key="author"
                            >
                                {{ author }}
                            </li>
                        </ul>
                    </div>
                    <p class="text-body-large text--typography1">
                        Last updated:
                        {{ new Date(libraryVersion.date).toDateString() }}
                    </p>
                    <p
                        class="text-body-large text--typography1"
                        v-if="calc.persistentCalcID"
                    >
                        Persistent Calc ID: {{ calc.persistentCalcID }}
                    </p>
                    <p class="text-body-large text--typography1">
                        Calc ID: {{ calc.calcId }}
                    </p>
                    <span
                        class="text-body-large text--typography1"
                        v-if="calc.tags && calc.tags.length"
                    >
                        Tags:
                    </span>
                    <v-chip pill v-for="tag in calc.tags" :key="tag">
                        {{ tag }}
                    </v-chip>
                </span>

                <span v-show="activeTab === 1">
                    <span v-if="calc.changeLog">
                        <p class="text-title-medium text--typography1">
                            Changelog
                        </p>
                        <div
                            v-for="(a, i) in calc.changeLog.slice().reverse()"
                            :key="i"
                        >
                            <p
                                class="text-title-small"
                                style="margin-bottom: 5px"
                            >
                                {{ a.revision }}
                            </p>
                            <p>{{ a.description }}</p>
                        </div>
                    </span>
                    <span v-else-if="calc.revisions">
                        <p class="text-title-medium text--typography1">
                            Revisions
                        </p>
                        <ul>
                            <li v-for="(a, i) in calc.revisions" :key="i">
                                v{{ a }}
                            </li>
                        </ul>
                    </span>
                    <p v-else class="text-body-large text--typography1">
                        This calculation has no revisions.
                    </p>
                </span>

                <span v-show="activeTab === 2">
                    <p class="text-title-medium text--typography1">
                        Assumptions
                    </p>
                    <p
                        v-if="!calc.assumptions"
                        class="text-body-large text--typography1"
                    >
                        This calculation has no assumptions.
                    </p>
                    <ul v-if="calc.assumptions">
                        <li
                            v-for="(a, i) in calc.assumptions"
                            :key="i"
                            class="mt-2 mb-2 text-wrap"
                        >
                            {{ a }}
                        </li>
                    </ul>
                </span>

                <span v-show="activeTab === 3">
                    <span v-if="stats.users">
                        <div v-if="usageStatsError.info" style="color: red">
                            Error loading user data
                        </div>
                        <div v-if="usageStatsError.image" style="color: red">
                            Error loading user images
                        </div>
                        <v-table>
                            <thead>
                                <tr>
                                    <th class="text-left">User</th>
                                    <th class="text-left">Times used</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(obj, i) in allUserInfoImages"
                                    :key="i"
                                >
                                    <td>
                                        <UserMenu
                                            :email="obj.email"
                                            :photo="obj.image"
                                            :user="obj.user"
                                        />
                                    </td>
                                    <td>{{ obj.count }}</td>
                                </tr>
                            </tbody>
                        </v-table>
                    </span>
                    <span v-else>
                        This calculation has not been used yet.
                    </span>
                </span>
            </div>
        </div>
    </span>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import RecordChip from '../pages/records/RecordChip.vue'
import UserMenu from '../UserMenu.vue'

const props = defineProps({
    calc: { type: Object, required: true },
    libraryVersion: { type: Object, required: true },
    show: { type: Boolean, default: true },
    duplicatedInfo: { required: false, default: false },
    defaultTab: { type: Number, required: false, default: 0 },
})

const store = useStore()

const activeTab = ref(props.defaultTab)
const tabs = [
    { id: 0, name: 'Information' },
    { id: 1, name: 'Version History' },
    { id: 2, name: 'Assumptions' },
    { id: 3, name: 'Usage Stats' },
]
const stats = ref({})
const allUserInfoImages = ref([])
const usageStatsError = ref({ info: false, image: false })
const userInfoSelected = ref(false)

const height = computed(() => (activeTab.value === 3 ? '300px' : '100%'))
const additionalAuthors = computed(() => props.calc.additionalAuthors ?? [])

function getBadge(tab) {
    switch (tab.id) {
        case 1:
            return props.calc.revisions ? props.calc.revisions.length : 0
        case 2:
            return props.calc.assumptions ? props.calc.assumptions.length : 0
        case 3:
            return stats.value ? stats.value.total : 0
        default:
            return 0
    }
}

watch(
    () => props.show,
    (visible) => {
        if (!visible) activeTab.value = 0
    }
)

watch(activeTab, async (tab) => {
    if (!userInfoSelected.value && tab === 3) {
        if (!stats.value)
            stats.value = await store.dispatch(
                'getCalcStats',
                props.calc.calcId
            )
        userInfoSelected.value = true
        let images = []
        let userInfo = false

        store
            .dispatch('getAllUserInfo', stats.value)
            .then(async (res) => {
                userInfo = true
                allUserInfoImages.value = res
                if (images.length > 0) {
                    allUserInfoImages.value = res.map((r, i) => ({
                        ...r,
                        image: images[i],
                    }))
                }
            })
            .catch((err) => {
                usageStatsError.value.info = true
                console.error(err)
            })

        store
            .dispatch('getAllUserImages', stats.value)
            .then((res) => {
                if (userInfo) {
                    allUserInfoImages.value = allUserInfoImages.value.map(
                        (a, i) => ({
                            ...a,
                            image:
                                res[i].status === 200
                                    ? 'data:image/jpeg;base64,' + res[i].body
                                    : undefined,
                        })
                    )
                } else {
                    images = res.map((r) =>
                        r.status === 200
                            ? 'data:image/jpeg;base64,' + r.body
                            : undefined
                    )
                }
            })
            .catch((err) => {
                usageStatsError.value.image = true
                console.error(err)
            })
    }
})

onMounted(async () => {
    const [user, s] = await Promise.all([
        store.dispatch('getUser', props.calc.authorEmail),
        store.dispatch('getCalcStats', props.calc.calcId),
    ])
    props.calc.user = user
    stats.value = s
    allUserInfoImages.value = s?.users?.map((u, i) => ({
        id: i + 1,
        email: u.name,
        count: u.count,
        image: undefined,
        user: undefined,
    }))
})
</script>
